import React, { useState, useEffect, useRef } from 'react';
import { FiUsers, FiLogOut, FiSearch, FiUser, FiCalendar, FiGift, FiInfo } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { database, ref as dbRef, onValue, set, get } from '../firebase';
import { storage, storageRef, uploadBytes, getDownloadURL } from '../firebase';
import { FaRupeeSign } from 'react-icons/fa';

const Dashboard = () => {
  const [activeSection, setActiveSection] = useState('users');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [events, setEvents] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    image: null,
  });
  const [giftForm, setGiftForm] = useState({
    giftId: null,
    watchPoints: '',
    sharePoints: '',
    likePoints: '',
    unlikePoints: '',
    invitePoints: '',
    purchasePoints: '',
    subscriptionPoints: '',
  });
  const [commissionForm, setCommissionForm] = useState({
    commissionId: null,
    watchVideoPoints: '',
    watchVideoTime: '',
    photoPoints: '',
    photoTime: '',
    videoPoints: '',
    youtubePoints: '',
    youtubeTime: '',
    instagramPoints: '',
    instagramTime: '',
    facebookPoints: '',
    facebookTime: '',
    googlePoints: '',
    googleTime: '',
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [giftExists, setGiftExists] = useState(false);
  const [commissionExists, setCommissionExists] = useState(false);
  const navigate = useNavigate();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef();
  const [editingEventId, setEditingEventId] = useState(null);
  const [editEventForm, setEditEventForm] = useState({
    title: '',
    description: '',
    image: null,
    imageUrl: null,
  });
  const [editImagePreview, setEditImagePreview] = useState(null);

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    }
    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileMenuOpen]);

  // Fetch users from Firebase
  useEffect(() => {
    const usersRef = dbRef(database, 'videoplayer/Users');

    const unsubscribeUsers = onValue(
      usersRef,
      (snapshot) => {
        const usersData = snapshot.val();
        if (usersData) {
          const usersArray = Object.entries(usersData).map(([id, user], index) => ({
            numericId: index + 1,
            id,
            ...user,
          }));
          setUsers(usersArray);
          setFilteredUsers(usersArray);
          setLoading(false);
        } else {
          setUsers([]);
          setFilteredUsers([]);
          setLoading(false);
        }
      },
      (error) => {
        console.error('Error fetching users:', error);
        setLoading(false);
      }
    );

    return () => unsubscribeUsers();
  }, []);

  // Fetch events from Firebase
  useEffect(() => {
    const eventsRef = dbRef(database, 'videoplayer/Events');
    const unsubscribeEvents = onValue(
      eventsRef,
      (snapshot) => {
        const eventsData = snapshot.val();
        if (eventsData) {
          const eventsArray = Object.entries(eventsData).map(([id, event], index) => ({
            numericId: index + 1,
            id,
            ...event,
          }));
          setEvents(eventsArray);
        } else {
          setEvents([]);
        }
      },
      (error) => {
        console.error('Error fetching events:', error);
      }
    );

    return () => unsubscribeEvents();
  }, []);

  // Fetch gifts from Firebase and pre-populate form if exists
  useEffect(() => {
    const giftsRef = dbRef(database, 'videoplayer/Gifts/1');
    const unsubscribeGifts = onValue(
      giftsRef,
      (snapshot) => {
        const giftData = snapshot.val();
        if (giftData) {
          setGifts([giftData]);
          setGiftExists(true);
          setGiftForm({
            giftId: 1,
            watchPoints: giftData.watchPoints || '',
            sharePoints: giftData.sharePoints || '',
            likePoints: giftData.likePoints || '',
            unlikePoints: giftData.unlikePoints || '',
            invitePoints: giftData.invitePoints || '',
            purchasePoints: giftData.purchasePoints || '',
            subscriptionPoints: giftData.subscriptionPoints || '',
            createdAt: giftData.createdAt,
          });
        } else {
          setGifts([]);
          setGiftExists(false);
        }
      },
      (error) => {
        console.error('Error fetching gifts:', error);
      }
    );

    return () => unsubscribeGifts();
  }, []);

  // Fetch commissions from Firebase and pre-populate form if exists
  useEffect(() => {
    const commissionsRef = dbRef(database, 'videoplayer/Commissions/1');
    const unsubscribeCommissions = onValue(
      commissionsRef,
      (snapshot) => {
        const commissionData = snapshot.val();
        if (commissionData) {
          setCommissions([commissionData]);
          setCommissionExists(true);
          setCommissionForm({
            commissionId: 1,
            watchVideoPoints: commissionData.watchVideoPoints || '',
            watchVideoTime: commissionData.watchVideoTime || '',
            photoPoints: commissionData.photoPoints || '',
            photoTime: commissionData.photoTime || '',
            videoPoints: commissionData.videoPoints || '',
            youtubePoints: commissionData.youtubePoints || '',
            youtubeTime: commissionData.youtubeTime || '',
            instagramPoints: commissionData.instagramPoints || '',
            instagramTime: commissionData.instagramTime || '',
            facebookPoints: commissionData.facebookPoints || '',
            facebookTime: commissionData.facebookTime || '',
            googlePoints: commissionData.googlePoints || '',
            googleTime: commissionData.googleTime || '',
            createdAt: commissionData.createdAt,
          });
        } else {
          setCommissions([]);
          setCommissionExists(false);
        }
      },
      (error) => {
        console.error('Error fetching commissions:', error);
      }
    );

    return () => unsubscribeCommissions();
  }, []);

  // Handle search query changes
  useEffect(() => {
    const lowerCaseQuery = searchQuery.toLowerCase();
    const filtered = users.filter(
      (user) =>
        (user.name && user.name.toLowerCase().includes(lowerCaseQuery)) ||
        (user.surname && user.surname.toLowerCase().includes(lowerCaseQuery))
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  // Summary data for cards
  const summary = {
    totalUsers: users.length,
    totalEvents: events.length,
    totalGifts: gifts.length,
    totalCommissions: commissions.length,
  };

  // Handle Accept/Reject actions for users
  const handleStatusUpdate = async (userId, status) => {
    try {
      const statusRef = dbRef(database, `videoplayer/Users/${userId}/status`);
      await set(statusRef, status);
      alert(`User ${status} successfully!`);
      setUsers(users.map((user) => (user.id === userId ? { ...user, status } : user)));
      setFilteredUsers(filteredUsers.map((user) => (user.id === userId ? { ...user, status } : user)));
    } catch (error) {
      console.error(`Error updating status for user ${userId}:`, error);
      alert('Failed to update status. Please try again.');
    }
  };

  // Handle profile navigation
  const handleViewProfile = (userId) => {
    navigate(`/admin/user/${userId}`);
  };

  // Handle event form input changes
  const handleEventFormChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      const file = files[0];
      setEventForm((prev) => ({ ...prev, [name]: file }));
      if (file) {
        const reader = new FileReader();
        reader.onload = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
    } else {
      setEventForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle gift form input changes
  const handleGiftFormChange = (e) => {
    const { name, value } = e.target;
    setGiftForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle commission form input changes
  const handleCommissionFormChange = (e) => {
    const { name, value } = e.target;
    setCommissionForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle event form submission
  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      const eventsRef = dbRef(database, 'videoplayer/Events');
      const snapshot = await get(eventsRef);
      const existingEvents = snapshot.val() || {};
      const numericIds = Object.keys(existingEvents)
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id));
      const nextId = numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1;

      let imageUrl = null;
      if (eventForm.image) {
        const titleFirstWord = eventForm.title.split(' ')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
        const imageName = titleFirstWord ? `${titleFirstWord}_${nextId}` : `event_${nextId}`;
        const imageRef = storageRef(storage, `videoplayer/Events/${imageName}`);
        await uploadBytes(imageRef, eventForm.image);
        imageUrl = await getDownloadURL(imageRef);
      }

      const eventData = {
        title: eventForm.title,
        description: eventForm.description,
        imageUrl,
        createdAt: new Date().toISOString(),
      };

      await set(dbRef(database, `videoplayer/Events/${nextId}`), eventData);
      alert('Event added successfully!');
      setEventForm({ title: '', description: '', image: null });
      setImagePreview(null);
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Failed to add event. Please try again.');
    }
  };

  // Handle gift form submission
  const handleGiftSubmit = async (e) => {
    e.preventDefault();
    if (giftExists && !giftForm.giftId) {
      alert('Gift configuration already exists. You can only update the existing configuration.');
      return;
    }
    try {
      const giftId = 1; // Fixed ID for single gift configuration
      const giftData = {
        giftId: giftId,
        watchPoints: parseInt(giftForm.watchPoints) || 0,
        sharePoints: parseInt(giftForm.sharePoints) || 0,
        likePoints: parseInt(giftForm.likePoints) || 0,
        unlikePoints: parseInt(giftForm.unlikePoints) || 0,
        invitePoints: parseInt(giftForm.invitePoints) || 0,
        purchasePoints: parseInt(giftForm.purchasePoints) || 0,
        subscriptionPoints: parseInt(giftForm.subscriptionPoints) || 0,
        createdAt: giftForm.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await set(dbRef(database, `videoplayer/Gifts/${giftId}`), giftData);
      alert(giftExists ? 'Gift points updated successfully!' : 'Gift points created successfully!');
      setGiftForm({
        giftId: giftId,
        watchPoints: giftData.watchPoints,
        sharePoints: giftData.sharePoints,
        likePoints: giftData.likePoints,
        unlikePoints: giftData.unlikePoints,
        invitePoints: giftData.invitePoints,
        purchasePoints: giftData.purchasePoints,
        subscriptionPoints: giftData.subscriptionPoints,
        createdAt: giftData.createdAt,
      });
      setGiftExists(true);
    } catch (error) {
      console.error('Error handling gift:', error);
      alert(`Failed to ${giftExists ? 'update' : 'create'} gift points. Please try again.`);
    }
  };

  // Handle commission form submission
  const handleCommissionSubmit = async (e) => {
    e.preventDefault();
    if (commissionExists && !commissionForm.commissionId) {
      alert('Commission configuration already exists. You can only update the existing configuration.');
      return;
    }
    try {
      const commissionId = 1; // Fixed ID for single commission configuration
      const commissionData = {
        commissionId: commissionId,
        watchVideoPoints: parseInt(commissionForm.watchVideoPoints) || 0,
        watchVideoTime: parseInt(commissionForm.watchVideoTime) || 0,
        photoPoints: parseInt(commissionForm.photoPoints) || 0,
        photoTime: parseInt(commissionForm.photoTime) || 0,
        youtubePoints: parseInt(commissionForm.youtubePoints) || 0,
        youtubeTime: parseInt(commissionForm.youtubeTime) || 0,
        instagramPoints: parseInt(commissionForm.instagramPoints) || 0,
        instagramTime: parseInt(commissionForm.instagramTime) || 0,
        facebookPoints: parseInt(commissionForm.facebookPoints) || 0,
        facebookTime: parseInt(commissionForm.facebookTime) || 0,
        googlePoints: parseInt(commissionForm.googlePoints) || 0,
        googleTime: parseInt(commissionForm.googleTime) || 0,
        createdAt: commissionForm.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await set(dbRef(database, `videoplayer/Commissions/${commissionId}`), commissionData);
      alert(commissionExists ? 'Commission points updated successfully!' : 'Commission points created successfully!');
      setCommissionForm({
        commissionId: commissionId,
        watchVideoPoints: commissionData.watchVideoPoints,
        watchVideoTime: commissionData.watchVideoTime,
        photoPoints: commissionData.photoPoints,
        photoTime: commissionData.photoTime,
        videoPoints: commissionData.videoPoints,
        youtubePoints: commissionData.youtubePoints,
        youtubeTime: commissionData.youtubeTime,
        instagramPoints: commissionData.instagramPoints,
        instagramTime: commissionData.instagramTime,
        facebookPoints: commissionData.facebookPoints,
        facebookTime: commissionData.facebookTime,
        googlePoints: commissionData.googlePoints,
        googleTime: commissionData.googleTime,
        createdAt: commissionData.createdAt,
      });
      setCommissionExists(true);
    } catch (error) {
      console.error('Error handling commission:', error);
      alert(`Failed to ${commissionExists ? 'update' : 'create'} commission points. Please try again.`);
    }
  };

  // Handle edit gift
  const handleEditGift = (gift) => {
    setGiftForm({
      giftId: gift.giftId,
      watchPoints: gift.watchPoints || '',
      sharePoints: gift.sharePoints || '',
      likePoints: gift.likePoints || '',
      unlikePoints: gift.unlikePoints || '',
      invitePoints: gift.invitePoints || '',
      purchasePoints: gift.purchasePoints || '',
      subscriptionPoints: gift.subscriptionPoints || '',
      createdAt: gift.createdAt,
    });
  };

  // Handle edit commission
  const handleEditCommission = (commission) => {
    setCommissionForm({
      commissionId: commission.commissionId,
      watchVideoPoints: commission.watchVideoPoints || '',
      watchVideoTime: commission.watchVideoTime || '',
      photoPoints: commission.photoPoints || '',
      photoTime: commission.photoTime || '',
      videoPoints: commission.videoPoints || '',
      youtubePoints: commission.youtubePoints || '',
      youtubeTime: commission.youtubeTime || '',
      instagramPoints: commission.instagramPoints || '',
      instagramTime: commission.instagramTime || '',
      facebookPoints: commission.facebookPoints || '',
      facebookTime: commission.facebookTime || '',
      googlePoints: commission.googlePoints || '',
      googleTime: commission.googleTime || '',
      createdAt: commission.createdAt,
    });
  };

  // Handle edit event button
  const handleEditEvent = (event) => {
    setEditingEventId(event.id);
    setEditEventForm({
      title: event.title,
      description: event.description,
      image: null,
      imageUrl: event.imageUrl || null,
    });
    setEditImagePreview(event.imageUrl || null);
  };

  // Handle edit event form changes
  const handleEditEventFormChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      const file = files[0];
      setEditEventForm((prev) => ({ ...prev, [name]: file }));
      if (file) {
        const reader = new FileReader();
        reader.onload = () => setEditImagePreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setEditImagePreview(editEventForm.imageUrl);
      }
    } else {
      setEditEventForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle update event
  const handleUpdateEvent = async (eventId, oldImageUrl) => {
    try {
      let imageUrl = editEventForm.imageUrl;
      // If a new image is selected, upload it
      if (editEventForm.image) {
        // Remove old image if exists
        if (oldImageUrl) {
          try {
            const oldImagePath = oldImageUrl.split('/o/')[1]?.split('?')[0]?.replace(/%2F/g, '/');
            if (oldImagePath) {
              const oldImageRef = storageRef(storage, decodeURIComponent(oldImagePath));
              await oldImageRef.delete();
            }
          } catch (err) {
            // Ignore error if image doesn't exist
          }
        }
        const titleFirstWord = editEventForm.title.split(' ')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
        const imageName = titleFirstWord ? `${titleFirstWord}_${eventId}` : `event_${eventId}`;
        const imageRef = storageRef(storage, `videoplayer/Events/${imageName}`);
        await uploadBytes(imageRef, editEventForm.image);
        imageUrl = await getDownloadURL(imageRef);
      }
      const eventData = {
        title: editEventForm.title,
        description: editEventForm.description,
        imageUrl,
        createdAt: events.find((ev) => ev.id === eventId)?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await set(dbRef(database, `videoplayer/Events/${eventId}`), eventData);
      alert('Event updated successfully!');
      setEditingEventId(null);
      setEditEventForm({ title: '', description: '', image: null, imageUrl: null });
      setEditImagePreview(null);
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event. Please try again.');
    }
  };

  // Handle delete event
  const handleDeleteEvent = async (eventId, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      // Delete image from storage if exists
      if (imageUrl) {
        try {
          const imagePath = imageUrl.split('/o/')[1]?.split('?')[0]?.replace(/%2F/g, '/');
          if (imagePath) {
            const imageRef = storageRef(storage, decodeURIComponent(imagePath));
            await imageRef.delete();
          }
        } catch (err) {
          // Ignore error if image doesn't exist
        }
      }
      // Delete event from database
      await set(dbRef(database, `videoplayer/Events/${eventId}`), null);
      alert('Event deleted successfully!');
      if (editingEventId === eventId) {
        setEditingEventId(null);
        setEditEventForm({ title: '', description: '', image: null, imageUrl: null });
        setEditImagePreview(null);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-5 flex items-center justify-between">
          {/* Left side - Dashboard Title */}
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight">Admin Dashboard</h1>
          
          {/* Right side - Welcome text and Logout button */}
          <div className="flex items-center space-x-4">
            {/* Admin Profile Icon with Menu */}
            <div className="relative" ref={profileMenuRef}>
              <button
                className="flex items-center px-3 sm:px-4 py-2 rounded-full bg-indigo-700 hover:bg-indigo-900 transition-colors duration-300 text-xs sm:text-sm font-semibold shadow-md hover:shadow-lg"
                onClick={() => setProfileMenuOpen((open) => !open)}
                title="Admin Profile"
              >
                <FiUser className="mr-2" />
                <span className="hidden sm:inline">Profile</span>
              </button>
              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50 animate-fadeIn" style={{ minWidth: 160 }}>
                  <button
                    className="flex items-center w-full text-left px-4 py-2 text-gray-700 hover:bg-indigo-50 rounded-t-lg gap-2"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      navigate('/admin/profile');
                    }}
                  >
                    <FiUser className="text-indigo-600 text-lg" />
                    <span>Profile</span>
                  </button>
                  <button
                    className="flex items-center w-full text-left px-4 py-2 text-gray-700 hover:bg-indigo-50 rounded-b-lg gap-2"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      localStorage.removeItem('currentUser');
                      navigate('/login');
                    }}
                  >
                    <FiLogOut className="text-red-500 text-lg" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Instruction Card/Button */}
        <div className="flex justify-start mb-4 sm:mb-6">
          <button
            onClick={() => navigate('/admin/instruction')}
            className="flex items-center px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-indigo-500 to-blue-500 text-white rounded-xl sm:rounded-2xl shadow-lg hover:scale-105 hover:shadow-xl transition-all duration-300 text-sm sm:text-lg font-semibold focus:outline-none focus:ring-4 focus:ring-indigo-300 min-h-[44px] sm:min-h-[48px] w-auto"
            style={{ animation: 'fadeIn 0.4s ease-in' }}
          >
            <FiInfo className="mr-2 sm:mr-3 text-lg sm:text-2xl" />
            <span className="hidden sm:inline">User Instructions</span>
            <span className="sm:hidden">Instructions</span>
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-10">
          {[
            { label: 'Total Users', value: summary.totalUsers, icon: <FiUsers className="text-indigo-500" />, color: 'bg-indigo-50' },
            { label: 'Total Events', value: summary.totalEvents, icon: <FiCalendar className="text-blue-500" />, color: 'bg-blue-50' },
          ].map((card, idx) => (
            <div
              key={card.label}
              className={`p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg ${card.color} transform hover:scale-105 transition-all duration-300`}
              style={{ animation: `fadeIn 0.3s ease-in ${idx * 0.1}s both` }}
            >
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="text-2xl sm:text-4xl">{card.icon}</div>
                <div>
                  <h3 className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider">{card.label}</h3>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{card.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Tabs */}
        <div className="mb-6 sm:mb-8 border-b border-gray-200 overflow-x-auto">
          <nav className="flex space-x-2 sm:space-x-4 min-w-max">
            {[
              { id: 'users', label: 'Users', icon: <FiUsers className="mr-1 sm:mr-2" /> },
              { id: 'events', label: 'Events', icon: <FiCalendar className="mr-1 sm:mr-2" /> },
              // { id: 'gifts', label: 'Extra commission', icon: <FiGift className="mr-1 sm:mr-2" /> },
              { id: 'commissions', label: 'Commissions', icon: <FaRupeeSign className="mr-1 sm:mr-2" /> },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold rounded-t-lg sm:rounded-t-xl transition-all duration-300 whitespace-nowrap ${
                  activeSection === item.id
                    ? 'bg-white text-indigo-600 border-b-4 border-indigo-600'
                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                }`}
                style={{ animation: 'fadeIn 0.3s ease-in' }}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8">
          {activeSection === 'users' && (
            <div style={{ animation: 'slideUp 0.5s ease-out' }}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FiUsers className="mr-3 text-indigo-600 text-3xl" /> Users
              </h2>
              {/* Search Bar */}
              <div className="mb-6">
                <div className="relative max-w-md">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or surname..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-300 pl-10"
                  />
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                </div>
              </div>
              {loading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-indigo-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ID</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Surname</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Mobile</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Plan</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">GST (%)</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Business</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Profile</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {filteredUsers.map((user, idx) => (
                        <tr key={user.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-indigo-50 transition-colors duration-200`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.numericId}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.name || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.surname || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.email || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.mobile || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.payment || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.plan || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.gst ? `${user.gst}%` : 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.business || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            <span
                              className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                user.status === 'accepted'
                                  ? 'bg-green-100 text-green-800'
                                  : user.status === 'rejected'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {user.status || 'Pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleViewProfile(user.id)}
                              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors duration-300 text-sm font-semibold shadow-md"
                            >
                              <FiUser className="mr-2" />
                              View Profile
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm flex space-x-2">
                            {user.status !== 'accepted' && (
                              <button
                                onClick={() => handleStatusUpdate(user.id, 'accepted')}
                                className="px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors duration-300 text-sm font-semibold shadow-md"
                              >
                                Accept
                              </button>
                            )}
                            {user.status !== 'rejected' && (
                              <button
                                onClick={() => handleStatusUpdate(user.id, 'rejected')}
                                className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors duration-300 text-sm font-semibold shadow-md"
                              >
                                Reject
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeSection === 'events' && (
            <div style={{ animation: 'slideUp 0.5s ease-out' }}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FiCalendar className="mr-3 text-blue-500 text-3xl" /> Events
              </h2>
              {/* Event Form */}
              {editingEventId ? (
                <div className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl shadow-lg transform hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6">Edit Event</h3>
                  <form onSubmit={e => { e.preventDefault(); handleUpdateEvent(editingEventId, events.find(ev => ev.id === editingEventId)?.imageUrl); }} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Title</label>
                      <input
                        type="text"
                        name="title"
                        value={editEventForm.title}
                        onChange={handleEditEventFormChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-300 bg-white shadow-sm"
                        placeholder="Enter event title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        name="description"
                        value={editEventForm.description}
                        onChange={handleEditEventFormChange}
                        required
                        rows="5"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-300 bg-white shadow-sm"
                        placeholder="Describe the event"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Image</label>
                      <input
                        type="file"
                        name="image"
                        accept="image/*"
                        onChange={handleEditEventFormChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 transition duration-300 bg-white shadow-sm"
                      />
                      {editImagePreview && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Image Preview</p>
                          <img src={editImagePreview} alt="Event Preview" className="h-40 w-40 object-cover rounded-lg shadow-md" />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-4">
                      <button
                        type="submit"
                        className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300 text-sm font-semibold shadow-md flex items-center justify-center"
                      >
                        <FiCalendar className="mr-2" />
                        Update Event
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingEventId(null);
                          setEditEventForm({ title: '', description: '', image: null, imageUrl: null });
                          setEditImagePreview(null);
                        }}
                        className="px-8 py-3 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors duration-300 text-sm font-semibold shadow-md flex items-center justify-center"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl shadow-lg transform hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6">Create New Event</h3>
                  <form onSubmit={handleEventSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Title</label>
                      <input
                        type="text"
                        name="title"
                        value={eventForm.title}
                        onChange={handleEventFormChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-300 bg-white shadow-sm"
                        placeholder="Enter event title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        name="description"
                        value={eventForm.description}
                        onChange={handleEventFormChange}
                        required
                        rows="5"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-300 bg-white shadow-sm"
                        placeholder="Describe the event"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Event Image</label>
                      <input
                        type="file"
                        name="image"
                        accept="image/*"
                        onChange={handleEventFormChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 transition duration-300 bg-white shadow-sm"
                      />
                      {imagePreview && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Image Preview</p>
                          <img src={imagePreview} alt="Event Preview" className="h-40 w-40 object-cover rounded-lg shadow-md" />
                        </div>
                      )}
                    </div>
                    <button
                      type="submit"
                      className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300 text-sm font-semibold shadow-md flex items-center justify-center"
                    >
                      <FiCalendar className="mr-2" />
                      Add Event
                    </button>
                  </form>
                </div>
              )}
              {/* Events Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Image</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created At</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {events.map((event, idx) => (
                      <tr key={event.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors duration-200`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{event.numericId}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{event.title}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{event.description}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {event.imageUrl ? (
                            <img src={event.imageUrl} alt={event.title} className="h-12 w-12 object-cover rounded" />
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{new Date(event.createdAt).toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm flex space-x-2">
                          <button
                            onClick={() => handleEditEvent(event)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors duration-300 text-sm font-semibold shadow-md"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteEvent(event.id, event.imageUrl)}
                            className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors duration-300 text-sm font-semibold shadow-md"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ------------------------------- Extra commission  ------------------------------- */}
          {activeSection === 'gifts' && (
            <div style={{ animation: 'slideUp 0.5s ease-out' }}>
              {/* <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FiGift className="mr-3 text-purple-500 text-3xl" /> Extra commission
              </h2> */}

              {/* Gift Form */}
              {/* <div className="mb-8 bg-gradient-to-br from-purple-50 to-indigo-50 p-8 rounded-xl shadow-lg transform hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">{giftExists ? 'Update Extra commission Points' : 'Create Gift Points'}</h3>
                {giftExists && (
                  <p className="text-sm text-gray-600 mb-4">Note: Only one Extra commission points configuration is allowed. Update the existing values below.</p>
                )}
                <form onSubmit={handleGiftSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Watching Points</label>
                      <input
                        type="number"
                        name="watchPoints"
                        value={giftForm.watchPoints}
                        onChange={handleGiftFormChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300 bg-white shadow-sm"
                        placeholder="Enter points for watching"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Share Points</label>
                      <input
                        type="number"
                        name="sharePoints"
                        value={giftForm.sharePoints}
                        onChange={handleGiftFormChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300 bg-white shadow-sm"
                        placeholder="Enter points for sharing"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Like Points</label>
                      <input
                        type="number"
                        name="likePoints"
                        value={giftForm.likePoints}
                        onChange={handleGiftFormChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300 bg-white shadow-sm"
                        placeholder="Enter points for liking"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Unlike Points</label>
                      <input
                        type="number"
                        name="unlikePoints"
                        value={giftForm.unlikePoints}
                        onChange={handleGiftFormChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300 bg-white shadow-sm"
                        placeholder="Enter points for unliking"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Invite Points</label>
                      <input
                        type="number"
                        name="invitePoints"
                        value={giftForm.invitePoints}
                        onChange={handleGiftFormChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300 bg-white shadow-sm"
                        placeholder="Enter points for inviting"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Points</label>
                      <input
                        type="number"
                        name="purchasePoints"
                        value={giftForm.purchasePoints}
                        onChange={handleGiftFormChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300 bg-white shadow-sm"
                        placeholder="Enter points for purchasing"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subscribe Points</label>
                      <input
                        type="number"
                        name="subscriptionPoints"
                        value={giftForm.subscriptionPoints}
                        onChange={handleGiftFormChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300 bg-white shadow-sm"
                        placeholder="Enter points for subscribing"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-8 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-300 text-sm font-semibold shadow-md flex items-center justify-center"
                  >
                    <FiGift className="mr-2" />
                    {giftExists ? 'Update Extra commission Points' : 'Create Extra commission Points'}
                  </button>
                </form>
              </div> */}

              {/* Gifts Table */}
              {/* <div className="overflow-x-auto">
                {gifts.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-purple-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ID</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Watching Points</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Share Points</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Like Points</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Unlike Points</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Invite Points</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Purchase Points</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Subscribe Points</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created At</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Updated At</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {gifts.map((gift, idx) => (
                        <tr key={gift.giftId} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-purple-50 transition-colors duration-200`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{gift.giftId}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{gift.watchPoints}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{gift.sharePoints}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{gift.likePoints}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{gift.unlikePoints}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{gift.invitePoints}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{gift.purchasePoints}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{gift.subscriptionPoints}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{new Date(gift.createdAt).toLocaleDateString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{gift.updatedAt ? new Date(gift.updatedAt).toLocaleDateString() : 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleEditGift(gift)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors duration-300 text-sm font-semibold shadow-md"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8">
                    <FiGift className="mx-auto text-4xl text-gray-400 mb-2" />
                    <p className="text-gray-500">No gift points configuration set yet</p>
                  </div>
                )}
              </div> */}
            </div>
          )}

           {/* ------------------------------- Extra commission End  ------------------------------- */}

          {activeSection === 'commissions' && (
            <div style={{ animation: 'slideUp 0.5s ease-out' }}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FaRupeeSign className="mr-3 text-green-500 text-3xl" /> Commissions
              </h2>
              {/* Commission Form */}
              <div className="mb-8 bg-gradient-to-br from-green-50 to-indigo-50 p-8 rounded-xl shadow-lg transform hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">{commissionExists ? 'Update Commission Points' : 'Create Commission Points'}</h3>
                {commissionExists && (
                  <p className="text-sm text-gray-600 mb-4">Note: Only one commission points configuration is allowed. Update the existing values below.</p>
                )}
                <form onSubmit={handleCommissionSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                    {/* Watch Video Section */}
                    <div className="bg-green-50 rounded-xl p-4 sm:p-6 shadow flex flex-col space-y-3 sm:space-y-4">
                      <h4 className="font-semibold text-green-700 text-base sm:text-lg mb-2 flex items-center"><FaRupeeSign className="mr-2" />Watch Video</h4>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Points</label>
                          <input
                            type="number"
                            name="watchVideoPoints"
                            value={commissionForm.watchVideoPoints}
                            onChange={handleCommissionFormChange}
                            required
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Points"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Time (Minutes)</label>
                          <input
                            type="number"
                            name="watchVideoTime"
                            value={commissionForm.watchVideoTime}
                            onChange={handleCommissionFormChange}
                            required
                            min="0"
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Time in minutes"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Photo Section */}
                    <div className="bg-blue-50 rounded-xl p-4 sm:p-6 shadow flex flex-col space-y-3 sm:space-y-4">
                      <h4 className="font-semibold text-blue-700 text-base sm:text-lg mb-2 flex items-center"><FaRupeeSign className="mr-2" />Photo</h4>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Points</label>
                          <input
                            type="number"
                            name="photoPoints"
                            value={commissionForm.photoPoints}
                            onChange={handleCommissionFormChange}
                            required
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Points"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Time (Minutes)</label>
                          <input
                            type="number"
                            name="photoTime"
                            value={commissionForm.photoTime}
                            onChange={handleCommissionFormChange}
                            required
                            min="0"
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Time in minutes"
                          />
                        </div>
                      </div>
                    </div>
                    {/* YouTube Section */}
                    <div className="bg-red-50 rounded-xl p-4 sm:p-6 shadow flex flex-col space-y-3 sm:space-y-4">
                      <h4 className="font-semibold text-red-700 text-base sm:text-lg mb-2 flex items-center"><FaRupeeSign className="mr-2" />YouTube</h4>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Points</label>
                          <input
                            type="number"
                            name="youtubePoints"
                            value={commissionForm.youtubePoints}
                            onChange={handleCommissionFormChange}
                            required
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Points"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Time (Minutes)</label>
                          <input
                            type="number"
                            name="youtubeTime"
                            value={commissionForm.youtubeTime}
                            onChange={handleCommissionFormChange}
                            required
                            min="0"
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Time in minutes"
                          />
                        </div>
                      </div>
                    </div>
                      {/* Google Section */}
                    <div className="bg-yellow-50 rounded-xl p-4 sm:p-6 shadow flex flex-col space-y-3 sm:space-y-4">
                      <h4 className="font-semibold text-yellow-700 text-base sm:text-lg mb-2 flex items-center"><FaRupeeSign className="mr-2" />Google</h4>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Points</label>
                          <input
                            type="number"
                            name="googlePoints"
                            value={commissionForm.googlePoints}
                            onChange={handleCommissionFormChange}
                            required
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Points"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Time (Minutes)</label>
                          <input
                            type="number"
                            name="googleTime"
                            value={commissionForm.googleTime}
                            onChange={handleCommissionFormChange}
                            required
                            min="0"
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Time in minutes"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Instagram Section */}
                    <div className="bg-pink-50 rounded-xl p-4 sm:p-6 shadow flex flex-col space-y-3 sm:space-y-4">
                      <h4 className="font-semibold text-pink-700 text-base sm:text-lg mb-2 flex items-center"><FaRupeeSign className="mr-2" />Instagram</h4>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Points</label>
                          <input
                            type="number"
                            name="instagramPoints"
                            value={commissionForm.instagramPoints}
                            onChange={handleCommissionFormChange}
                            required
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Points"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Time (Minutes)</label>
                          <input
                            type="number"
                            name="instagramTime"
                            value={commissionForm.instagramTime}
                            onChange={handleCommissionFormChange}
                            required
                            min="0"
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Time in minutes"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Facebook Section */}
                    <div className="bg-indigo-50 rounded-xl p-4 sm:p-6 shadow flex flex-col space-y-3 sm:space-y-4">
                      <h4 className="font-semibold text-indigo-700 text-base sm:text-lg mb-2 flex items-center"><FaRupeeSign className="mr-2" />Facebook</h4>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Points</label>
                          <input
                            type="number"
                            name="facebookPoints"
                            value={commissionForm.facebookPoints}
                            onChange={handleCommissionFormChange}
                            required
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Points"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Time (Minutes)</label>
                          <input
                            type="number"
                            name="facebookTime"
                            value={commissionForm.facebookTime}
                            onChange={handleCommissionFormChange}
                            required
                            min="0"
                            className="w-full px-3 py-2 sm:px-4 sm:py-2 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white shadow-sm text-xs sm:text-sm"
                            placeholder="Time in minutes"
                          />
                        </div>
                      </div>
                    </div>                 
                  </div>
                  <div className="flex justify-center mt-8">
                    <button
                      type="submit"
                      className="px-8 sm:px-10 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300 text-sm font-semibold shadow-md flex items-center justify-center"
                    >
                      <FaRupeeSign className="mr-2" />
                      {commissionExists ? 'Update Commission Points' : 'Create Commission Points'}
                    </button>
                  </div>
                </form>
              </div>
              {/* Commissions Table */}
              {commissions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-[900px] divide-y divide-gray-300 text-xs sm:text-sm" style={{ minWidth: 900 }}>
                    <thead className="bg-green-50">
                      <tr>
                        <th className="px-2 py-2 sm:px-6 sm:py-4">ID</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4">Watch Video Points</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-100">Watch Video Time (Minutes)</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4">Photo Points</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-100">Photo Time (Minutes)</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4">YouTube Points</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-100">YouTube Time (Minutes)</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4">Instagram Points</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-100">Instagram Time (Minutes)</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4">Facebook Points</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-100">Facebook Time (Minutes)</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4">Google Points</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-100">Google Time (Minutes)</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4 hidden md:table-cell">Created At</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4 hidden md:table-cell">Updated At</th>
                        <th className="px-2 py-2 sm:px-6 sm:py-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((commission, idx) => (
                        <tr key={commission.commissionId} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-green-50 transition-colors duration-200`}>
                          <td className="px-2 py-2 sm:px-6 sm:py-4">{commission.commissionId}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4">{commission.watchVideoPoints}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-50">{commission.watchVideoTime}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4">{commission.photoPoints}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-50">{commission.photoTime}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4">{commission.youtubePoints}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-50">{commission.youtubeTime}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4">{commission.instagramPoints}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-50">{commission.instagramTime}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4">{commission.facebookPoints}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-50">{commission.facebookTime}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4">{commission.googlePoints}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4 bg-yellow-50">{commission.googleTime}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4 hidden md:table-cell">{new Date(commission.createdAt).toLocaleDateString()}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4 hidden md:table-cell">{commission.updatedAt ? new Date(commission.updatedAt).toLocaleDateString() : 'N/A'}</td>
                          <td className="px-2 py-2 sm:px-6 sm:py-4">
                            <button
                              onClick={() => handleEditCommission(commission)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors duration-300 text-xs sm:text-sm font-semibold shadow-md"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaRupeeSign className="mx-auto text-4xl text-gray-400 mb-2" />
                  <p className="text-gray-500">No commission points configuration set yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Inline CSS for Animations and Mobile Responsive Design */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        /* Mobile Responsive Styles - All Screen Sizes */
        
        /* Instruction Button Mobile Styles */
        @media (max-width: 640px) {
          /* Instruction button container */
          .flex.justify-start.mb-4 {
            margin-bottom: 1rem;
          }
          
          /* Instruction button mobile optimization */
          .flex.justify-start.mb-4 button {
            padding: 0.75rem 1rem;
            font-size: 0.875rem;
            border-radius: 0.75rem;
            min-height: 44px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
          
          /* Instruction button icon */
          .flex.justify-start.mb-4 button svg {
            width: 1.25rem;
            height: 1.25rem;
            margin-right: 0.5rem;
          }
          
          /* Instruction button text */
          .flex.justify-start.mb-4 button span {
            font-weight: 600;
            letter-spacing: 0.025em;
          }
        }
        
        /* Extra Large Mobile (481px - 640px) */
        @media (min-width: 481px) and (max-width: 640px) {
          .max-w-7xl {
            max-width: 100%;
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }
          
          header h1 {
            font-size: 1.75rem;
          }
          
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.5rem;
          }
          
          nav.flex {
            justify-content: center;
            gap: 0.75rem;
          }
          
          nav button {
            font-size: 0.875rem;
            padding: 0.75rem 1.5rem;
          }
          
          table {
            min-width: 900px;
            font-size: 0.875rem;
          }
          
          th, td {
            padding: 0.75rem 0.5rem;
          }
        }

        /* Large Mobile (401px - 480px) */
        @media (min-width: 401px) and (max-width: 480px) {
          .max-w-7xl {
            max-width: 100%;
            padding-left: 1.25rem;
            padding-right: 1.25rem;
          }
          
          header h1 {
            font-size: 1.5rem;
          }
          
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.25rem;
          }
          
          nav.flex {
            justify-content: center;
            gap: 0.5rem;
          }
          
          nav button {
            font-size: 0.8rem;
            padding: 0.625rem 1.25rem;
          }
          
          table {
            min-width: 850px;
            font-size: 0.8rem;
          }
          
          th, td {
            padding: 0.625rem 0.375rem;
          }
        }

        /* Medium Mobile (361px - 400px) */
        @media (min-width: 361px) and (max-width: 400px) {
          .max-w-7xl {
            max-width: 100%;
            padding-left: 1rem;
            padding-right: 1rem;
          }
          
          /* Instruction button for medium mobile */
          .flex.justify-start.mb-4 button {
            padding: 0.625rem 0.875rem;
            font-size: 0.8rem;
            border-radius: 0.625rem;
            min-height: 42px;
          }
          
          .flex.justify-start.mb-4 button svg {
            width: 1.125rem;
            height: 1.125rem;
            margin-right: 0.4375rem;
          }
          
          header h1 {
            font-size: 1.375rem;
          }
          
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          
          nav.flex {
            flex-direction: column;
            gap: 0.375rem;
          }
          
          nav button {
            font-size: 0.75rem;
            padding: 0.5rem 1rem;
            width: 100%;
          }
          
          table {
            min-width: 750px;
            font-size: 0.75rem;
          }
          
          th, td {
            padding: 0.5rem 0.25rem;
          }
        }

        /* Small Mobile (321px - 360px) */
        @media (min-width: 321px) and (max-width: 360px) {
          .max-w-7xl {
            max-width: 100%;
            padding-left: 0.875rem;
            padding-right: 0.875rem;
          }
          
          header h1 {
            font-size: 1.25rem;
          }
          
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: 1fr;
            gap: 0.875rem;
          }
          
          nav.flex {
            flex-direction: column;
            gap: 0.25rem;
          }
          
          nav button {
            font-size: 0.7rem;
            padding: 0.375rem 0.875rem;
            width: 100%;
          }
          
          table {
            min-width: 700px;
            font-size: 0.7rem;
          }
          
          th, td {
            padding: 0.375rem 0.125rem;
          }
          
          .text-2xl {
            font-size: 1.375rem;
          }
          
          .text-xl {
            font-size: 1.25rem;
          }
        }

        /* Extra Small Mobile (≤320px) */
        @media (max-width: 320px) {
          .max-w-7xl {
            max-width: 100%;
            padding-left: 0.75rem;
            padding-right: 0.75rem;
          }
          
          /* Instruction button for very small screens */
          .flex.justify-start.mb-4 button {
            padding: 0.5rem 0.75rem;
            font-size: 0.75rem;
            border-radius: 0.5rem;
            min-height: 40px;
          }
          
          .flex.justify-start.mb-4 button svg {
            width: 1rem;
            height: 1rem;
            margin-right: 0.375rem;
          }
          
          header h1 {
            font-size: 1.125rem;
          }
          
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }
          
          nav.flex {
            flex-direction: column;
            gap: 0.25rem;
          }
          
          nav button {
            font-size: 0.65rem;
            padding: 0.25rem 0.75rem;
            width: 100%;
          }
          
          table {
            min-width: 650px;
            font-size: 0.65rem;
          }
          
          th, td {
            padding: 0.25rem 0.125rem;
          }
          
          .text-2xl {
            font-size: 1.25rem;
          }
          
          .text-xl {
            font-size: 1.125rem;
          }
          
          .text-lg {
            font-size: 1rem;
          }
          
          .p-8 {
            padding: 0.75rem;
          }
          
          .px-4 {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          
          .py-8 {
            padding-top: 0.75rem;
            padding-bottom: 0.75rem;
          }
        }

        /* Standard Mobile (≤640px) - Fallback */
        @media (max-width: 640px) {
          .max-w-7xl {
            max-width: 100%;
            padding-left: 1rem;
            padding-right: 1rem;
          }
          
          /* Header Responsive */
          header .flex {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            gap: 0.5rem;
          }
          
          /* Header title responsive */
          header h1 {
            font-size: 1.25rem;
            text-align: left;
          }
          
          /* Logout button mobile optimization */
          header button {
            padding: 0.5rem 0.75rem;
            font-size: 0.75rem;
            min-width: auto;
          }
          
          /* Summary Cards */
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          
          /* Navigation Tabs */
          nav.flex {
            flex-wrap: wrap;
            gap: 0.5rem;
          }
          
          nav button {
            font-size: 0.75rem;
            padding: 0.5rem 1rem;
            white-space: nowrap;
          }
          
          /* Table Responsive */
          .overflow-x-auto {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          table {
            min-width: 800px;
          }
          
          /* Form Responsive */
          .grid.grid-cols-1.sm\\:grid-cols-2 {
            grid-template-columns: 1fr;
          }
          
          .grid.grid-cols-1.md\\:grid-cols-2 {
            grid-template-columns: 1fr;
          }
          
          /* Commission Form Sections */
          .bg-green-50, .bg-blue-50, .bg-red-50, .bg-pink-50, .bg-indigo-50, .bg-yellow-50 {
            margin-bottom: 1rem;
          }
          
          /* Button Responsive */
          button {
            width: 100%;
            margin-bottom: 0.5rem;
          }
          
          /* Search Bar */
          .max-w-md {
            max-width: 100%;
          }
        }

        @media (max-width: 768px) {
          /* Tablet Styles */
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: repeat(2, 1fr);
          }
          
          nav.flex {
            justify-content: center;
          }
          
          /* Commission Form */
          .grid.grid-cols-1.md\\:grid-cols-2 {
            grid-template-columns: 1fr;
          }
          
          /* Table adjustments */
          table {
            font-size: 0.75rem;
          }
          
          th, td {
            padding: 0.5rem 0.25rem;
          }
        }

        @media (max-width: 1024px) {
          /* Small Desktop */
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: repeat(2, 1fr);
          }
          
          /* Commission Form */
          .grid.grid-cols-1.md\\:grid-cols-2 {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 480px) {
          /* Extra Small Mobile */
          header h1 {
            font-size: 1.25rem;
          }
          
          .text-2xl {
            font-size: 1.5rem;
          }
          
          .text-xl {
            font-size: 1.25rem;
          }
          
          /* Navigation */
          nav.flex {
            flex-direction: column;
            gap: 0.25rem;
          }
          
          nav button {
            width: 100%;
            text-align: center;
          }
          
          /* Table */
          table {
            min-width: 600px;
            font-size: 0.7rem;
          }
          
          th, td {
            padding: 0.25rem 0.125rem;
          }
          
          /* Form inputs */
          input, textarea, select {
            font-size: 16px; /* Prevents zoom on iOS */
          }
          
          /* Buttons */
          button {
            font-size: 0.875rem;
            padding: 0.75rem 1rem;
          }
        }

        @media (max-width: 320px) {
          /* Very Small Mobile */
          .p-8 {
            padding: 1rem;
          }
          
          .px-4 {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          
          .py-8 {
            padding-top: 1rem;
            padding-bottom: 1rem;
          }
          
          /* Text sizes */
          .text-2xl {
            font-size: 1.25rem;
          }
          
          .text-xl {
            font-size: 1.125rem;
          }
          
          .text-lg {
            font-size: 1rem;
          }
        }

        /* Landscape Mobile */
        @media (max-width: 768px) and (orientation: landscape) {
          header .flex {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }
          
          header h1 {
            font-size: 1.5rem;
          }
          
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: repeat(4, 1fr);
          }
          
          nav.flex {
            flex-direction: row;
            flex-wrap: wrap;
          }
        }

        /* Portrait Mobile Landscape */
        @media (max-width: 640px) and (orientation: landscape) {
          header .flex {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            gap: 1rem;
          }
          
          header h1 {
            font-size: 1.25rem;
          }
          
          header button {
            padding: 0.5rem 1rem;
            font-size: 0.8rem;
          }
          
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.75rem;
          }
          
          nav.flex {
            flex-direction: row;
            gap: 0.5rem;
          }
          
          nav button {
            font-size: 0.7rem;
            padding: 0.375rem 0.75rem;
          }
        }

        /* Very Small Landscape */
        @media (max-width: 480px) and (orientation: landscape) {
          header h1 {
            font-size: 1.125rem;
          }
          
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.5rem;
          }
          
          nav button {
            font-size: 0.65rem;
            padding: 0.25rem 0.5rem;
          }
        }

        /* High DPI Screens */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          /* Ensure crisp text on high DPI displays */
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
        }

        /* Touch Device Optimizations */
        @media (hover: none) and (pointer: coarse) {
          /* Increase touch targets for better mobile experience */
          button, input, select, textarea {
            min-height: 44px;
            min-width: 44px;
          }
          
          /* Instruction button touch optimization */
          .flex.justify-start.mb-4 button {
            min-height: 48px;
            min-width: 48px;
            padding: 0.875rem 1.25rem;
            border-radius: 0.875rem;
            box-shadow: 0 6px 12px -2px rgba(0, 0, 0, 0.15);
          }
          
          /* Better touch feedback for instruction button */
          .flex.justify-start.mb-4 button:active {
            transform: scale(0.95);
            box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
            transition: all 0.1s ease;
          }
          
          /* Larger touch areas for table interactions */
          td, th {
            min-height: 44px;
            padding: 0.75rem 0.5rem;
          }
          
          /* Better touch feedback */
          button:active, input:active, select:active {
            transform: scale(0.98);
            transition: transform 0.1s ease;
          }
        }

        /* Device-Specific Optimizations */
        
        /* iPhone SE and similar small devices */
        @media (max-width: 375px) and (max-height: 667px) {
          .p-8 {
            padding: 0.5rem;
          }
          
          .py-8 {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
          }
          
          header h1 {
            font-size: 1.125rem;
          }
          
          .text-2xl {
            font-size: 1.25rem;
          }
          
          /* Instruction button for iPhone SE */
          .flex.justify-start.mb-4 button {
            padding: 0.625rem 1rem;
            font-size: 0.8rem;
            border-radius: 0.625rem;
            min-height: 44px;
          }
          
          .flex.justify-start.mb-4 button svg {
            width: 1.125rem;
            height: 1.125rem;
            margin-right: 0.5rem;
          }
        }

        /* iPhone 12/13/14 Pro Max and similar large phones */
        @media (min-width: 428px) and (max-width: 480px) {
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: repeat(2, 1fr);
          }
          
          nav.flex {
            justify-content: center;
            flex-direction: row;
          }
        }

        /* Samsung Galaxy Fold and similar foldables */
        @media (max-width: 280px) {
          .max-w-7xl {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          
          header h1 {
            font-size: 1rem;
          }
          
          .text-2xl {
            font-size: 1.125rem;
          }
          
          .text-xl {
            font-size: 1rem;
          }
          
          nav button {
            font-size: 0.6rem;
            padding: 0.25rem 0.5rem;
          }
          
          table {
            min-width: 600px;
            font-size: 0.6rem;
          }
        }

        /* iPad and similar tablets in portrait */
        @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: repeat(2, 1fr);
          }
          
          nav.flex {
            justify-content: center;
          }
        }

        /* iPad and similar tablets in landscape */
        @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
          .grid-cols-1.sm\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        /* Print Styles */
        @media print {
          header, nav, button {
            display: none;
          }
          
          .bg-white {
            background: white !important;
          }
          
          table {
            border-collapse: collapse;
          }
          
          th, td {
            border: 1px solid #000;
            padding: 0.5rem;
          }
        }

        /* Accessibility */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Mobile Performance Optimizations */
        @media (max-width: 768px) {
          /* Optimize animations for mobile */
          .transform {
            will-change: transform;
          }
          
          /* Reduce shadow complexity on mobile */
          .shadow-lg, .shadow-xl {
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }
          
          /* Optimize table rendering */
          table {
            contain: layout style paint;
          }
          
          /* Reduce gradient complexity */
          .bg-gradient-to-r {
            background: linear-gradient(to right, var(--tw-gradient-stops));
          }
        }

        /* Mobile Battery Optimization */
        @media (max-width: 768px) {
          /* Reduce unnecessary repaints */
          .hover\\:scale-105:hover {
            transform: scale(1.02);
          }
          
          /* Optimize transitions */
          .transition-all {
            transition: all 0.2s ease;
          }
          
          /* Reduce animation complexity */
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from { 
              transform: translateY(10px);
              opacity: 0;
            }
            to { 
              transform: translateY(0);
              opacity: 1;
            }
          }
        }

        /* Mobile Network Optimization */
        @media (max-width: 768px) {
          /* Reduce image sizes for faster loading */
          img {
            max-width: 100%;
            height: auto;
          }
          
          /* Optimize font loading */
          * {
            font-display: swap;
          }
        }

        /* Mobile Gesture Support */
        @media (max-width: 768px) {
          /* Enable smooth scrolling */
          html {
            scroll-behavior: smooth;
          }
          
          /* Better touch scrolling */
          .overflow-x-auto {
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          
          .overflow-x-auto::-webkit-scrollbar {
            display: none;
          }
        }

        /* Dark Mode Support */
        @media (prefers-color-scheme: dark) {
          .bg-gray-100 {
            background-color: #1f2937;
          }
          
          .bg-white {
            background-color: #374151;
          }
          
          .text-gray-900 {
            color: #f9fafb;
          }
          
          .text-gray-700 {
            color: #d1d5db;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;